import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useState } from 'react'

import { SEARCH_PARAM_KEYS } from '@/constants/search'
import { useRouteChangeContext } from '@/context/RouteChangeContext'
import { useUAContext } from '@/context/UAContext'
import useHash from '@/hooks/useHash'
import { getDeviceValue, isValidDevice, replaceUrlWithQueryString } from '@/lib/search'
import { DeviceValue } from '@/types/search'

export const useSearchFilters = () => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const hash = useHash()
  const [showFilters, setShowFilters] = useState(false)
  const { setIsRouteLoading } = useRouteChangeContext()
  const { deviceTypeWithAndroidAndIOS } = useUAContext()
  const defaultDevice = getDeviceValue(deviceTypeWithAndroidAndIOS) ?? 'desktop'
  const deviceParam = searchParams.get(SEARCH_PARAM_KEYS.DEVICE) || defaultDevice
  const device = (isValidDevice(deviceParam) ? deviceParam : defaultDevice) as DeviceValue

  const tag = searchParams.get(SEARCH_PARAM_KEYS.GENRE) || ''
  const type = searchParams.get(SEARCH_PARAM_KEYS.TYPE) || 'all'

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setIsRouteLoading(true)

      const params = new URLSearchParams(searchParams.toString())

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '') {
          params.delete(key)
        } else {
          params.set(key, value)
        }
      })

      const queryString = params.toString()
      replaceUrlWithQueryString(router, pathname, queryString, hash, false)
    },
    [router, pathname, searchParams, setIsRouteLoading, hash],
  )

  const handleDeviceChange = useCallback(
    (value: DeviceValue) => {
      updateSearchParams({ [SEARCH_PARAM_KEYS.DEVICE]: value })
    },
    [updateSearchParams],
  )

  const handleFilterChange = useCallback(
    (value: string) => {
      updateSearchParams({ [SEARCH_PARAM_KEYS.GENRE]: value === '' ? null : value })
    },
    [updateSearchParams],
  )

  const handleTypeChange = useCallback(
    (value: string) => {
      updateSearchParams({
        [SEARCH_PARAM_KEYS.TYPE]: value === '' || value === 'all' ? null : value,
      })
    },
    [updateSearchParams],
  )

  const handleClearAllFilters = useCallback(() => {
    updateSearchParams({
      [SEARCH_PARAM_KEYS.DEVICE]: null,
      [SEARCH_PARAM_KEYS.GENRE]: null,
      [SEARCH_PARAM_KEYS.TYPE]: null,
    })
  }, [updateSearchParams])

  return {
    showFilters,
    setShowFilters,
    device,
    tag,
    type,
    updateSearchParams,
    handleDeviceChange,
    handleFilterChange,
    handleTypeChange,
    handleClearAllFilters,
  }
}
